/**
 * Category Controller
 * Handles all categories-related API calls and data transformations
 */

import axios from 'axios';
import { getActiveStoreId } from '../../../services/storeService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
let allCategoriesCache = null;
let allCategoriesCacheAt = 0;
let allCategoriesPromise = null;
// Which store the cached/in-flight "all categories" result belongs to.
let allCategoriesCacheScope = null;
let allCategoriesPromiseScope = null;
const ALL_CATEGORIES_CACHE_MS = 5 * 60 * 1000;
const paginatedCategoriesCache = new Map();
const paginatedCategoriesPromise = new Map();

// Add this helper function to get auth headers
const getAuthHeaders = () => {
  const authToken = localStorage.getItem('authToken');
  return {
    Authorization: authToken ? `Bearer ${authToken}` : ''
  };
};

const CategoryController = {
  /**
   * Fetch categories with optional filtering and pagination
   * @param {number} pageNumber - Current page number
   * @param {number} size - Items per page
   * @returns {Promise<Object>} - Paginated category data
   */
  fetchCategories: async (pageNumber = 1, size = 10, ) => {
    try {
      const storeId = await getActiveStoreId();
      const cacheKey = `${pageNumber}:${size}:${storeId || 'all'}`;
      const cached = paginatedCategoriesCache.get(cacheKey);
      if (cached && Date.now() - cached.at < ALL_CATEGORIES_CACHE_MS) {
        return cached.data;
      }

      if (paginatedCategoriesPromise.has(cacheKey)) {
        return await paginatedCategoriesPromise.get(cacheKey);
      }

      let url = `${API_URL}/Categorie/Pagination?Size=${size}&PageNumber=${pageNumber}`;
      if (storeId) url += `&BoutiqueId=${encodeURIComponent(storeId)}`;
      
      // Only change: Add headers to the request
      const request = axios.get(url, { headers: getAuthHeaders() }).then((response) => {
        paginatedCategoriesCache.set(cacheKey, { data: response.data, at: Date.now() });
        return response.data;
      }).finally(() => {
        paginatedCategoriesPromise.delete(cacheKey);
      });

      paginatedCategoriesPromise.set(cacheKey, request);
      return await request;
    } catch (error) {
      console.error('Error fetching Categories:', error);
      throw error;
    }
    
  },


   /**
   * Get all Categorys (without pagination)
   * @returns {Promise<Array>} - All Categories
   */

  fetchAllCategories: async () => {
    try {
      // Scoped to the active store: the admin's selected store, or the store that
      // owns the current domain on the storefront.
      const storeId = await getActiveStoreId();
      const cacheScope = storeId || 'all';

      if (allCategoriesCache && allCategoriesCacheScope === cacheScope && Date.now() - allCategoriesCacheAt < ALL_CATEGORIES_CACHE_MS) {
        return allCategoriesCache;
      }

      if (allCategoriesPromise && allCategoriesPromiseScope === cacheScope) {
        return await allCategoriesPromise;
      }

      let url = `${API_URL}/Categorie`;
      if (storeId) url += `?BoutiqueId=${encodeURIComponent(storeId)}`;

      // Only change: Add headers to the request
      allCategoriesPromiseScope = cacheScope;
      allCategoriesPromise = axios.get(url, { headers: getAuthHeaders() }).then((response) => {
        allCategoriesCache = response.data;
        allCategoriesCacheAt = Date.now();
        allCategoriesCacheScope = cacheScope;
        return response.data;
      }).finally(() => {
        allCategoriesPromise = null;
      });

      return await allCategoriesPromise;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },
  
  /**
   * Create a new Category
   * @param {Object} CategoryData - Contains name and other Category properties
   * @returns {Promise<Object>} - Created Category data
   */
  createCategory: async (CategoryData, imageFile = null) => {
    try {
      const formData = new FormData();
      formData.append('name', CategoryData.name);
      if (CategoryData.pixelBalise) formData.append('pixelBalise', CategoryData.pixelBalise);
      if (CategoryData.pixelId) formData.append('pixelId', CategoryData.pixelId);
      if (CategoryData.pixelToken) formData.append('pixelToken', CategoryData.pixelToken);
      if (CategoryData.fbAppId) formData.append('fbAppId', CategoryData.fbAppId);
      // Create the category inside the store currently selected in the admin.
      const storeId = CategoryData.boutiqueId ?? await getActiveStoreId();
      if (storeId) formData.append('boutiqueId', storeId);
      if (imageFile) formData.append('image', imageFile);

      const authToken = localStorage.getItem('authToken');
      const response = await axios({
        method: 'post',
        url: `${API_URL}/Categorie/save`,
        data: formData,
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : ''
        }
      });
      // Invalidate caches so list refreshes
      allCategoriesCache = null;
      allCategoriesCacheScope = null;
      paginatedCategoriesCache.clear();
      return response.data;
    } catch (error) {
      console.error('Error creating Category:', error);
      throw error;
    }
  },
  
  /**
   * Update an existing Category
   * @param {Object} CategoryData - Contains id and name of Category to update
   * @returns {Promise<Object>} - Updated Category data
   */
  updateCategory: async (CategoryData, imageFile = null) => {
    try {
      const formData = new FormData();
      formData.append('id', CategoryData.id);
      formData.append('name', CategoryData.name);
      if (CategoryData.pixelBalise) formData.append('pixelBalise', CategoryData.pixelBalise);
      if (CategoryData.pixelId) formData.append('pixelId', CategoryData.pixelId);
      if (CategoryData.pixelToken) formData.append('pixelToken', CategoryData.pixelToken);
      if (CategoryData.fbAppId) formData.append('fbAppId', CategoryData.fbAppId);
      if (CategoryData.imagePath) formData.append('existingImagePath', CategoryData.imagePath);
      // Omitted on edit => the backend keeps the category in its current store.
      if (CategoryData.boutiqueId != null) formData.append('boutiqueId', CategoryData.boutiqueId);
      if (imageFile) formData.append('image', imageFile);

      const authToken = localStorage.getItem('authToken');
      const response = await axios({
        method: 'post',
        url: `${API_URL}/Categorie/save`,
        data: formData,
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : ''
        }
      });
      // Invalidate caches so list refreshes
      allCategoriesCache = null;
      allCategoriesCacheScope = null;
      paginatedCategoriesCache.clear();
      return response.data;
    } catch (error) {
      console.error(`Error updating Category #${CategoryData.id}:`, error);
      throw error;
    }
  },
  
  /**
   * Delete a Category
   * @param {number} CategoryId - ID of Category to delete
   * @returns {Promise<boolean>} - Success indicator
   */
  deleteCategory: async (CategoryId) => {
    try {
      // Only change: Add headers to the request
      await axios.delete(`${API_URL}/Categorie/${CategoryId}`, { headers: getAuthHeaders() });
      // Invalidate caches so list refreshes
      allCategoriesCache = null;
      allCategoriesCacheScope = null;
      paginatedCategoriesCache.clear();
      return true;
    } catch (error) {
      console.error(`Error deleting Category #${CategoryId}:`, error);
      throw error;
    }
  },
  
  
  /**
   * Get a single Category by ID (new endpoint)
   * @param {number} id - ID of Category to fetch
   * @returns {Promise<Object>} - Category data
   */
  getCategoryById: async (id) => {
    try {
      // Only change: Add headers to the request
      const response = await axios.get(`${API_URL}/Categorie/ById/${id}`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      console.error(`Error fetching Category #${id}:`, error);
      throw error;
    }
  },
  
 

};

export default CategoryController;